import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  User,
  Mail,
  Phone,
  MoreHorizontal,
  Download,
  Search,
  Ban,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
  Users,
  MailX,
  ArrowUpDown,
  Plus,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Member {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  is_blocked: boolean;
  created_at: string;
  appointment_count?: number;
  email_confirmed?: boolean;
  unsubscribed?: boolean;
  member_type: 'consulente' | 'filho' | null;
  member_function: string | null;
  custom_function: string | null;
  pai_de_cabeca: string | null;
  mae_de_cabeca: string | null;
  is_admin?: boolean;
}

interface MemberAppointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  notes: string | null;
}

interface CustomFunction {
  id: string;
  name: string;
}

type SortField = "full_name" | "created_at" | "appointment_count";
type SortOrder = "asc" | "desc";
type FilterType = "all" | "active" | "blocked" | "unsubscribed" | "consulente" | "filho";

const MEMBER_FUNCTIONS = [
  { value: "cambone", label: "Cambone" },
  { value: "curimba", label: "Curimba" },
  { value: "medium_atendimento", label: "Médium de Atendimento" },
  { value: "medium_passe", label: "Médium de Passe" },
  { value: "medium_firmado", label: "Médium Firmado" },
];

export const MembersTab = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [memberAppointments, setMemberAppointments] = useState<MemberAppointment[]>([]);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [customFunctions, setCustomFunctions] = useState<CustomFunction[]>([]);
  const [newFunctionName, setNewFunctionName] = useState("");
  const [showAddFunctionDialog, setShowAddFunctionDialog] = useState(false);
  const [isSavingMemberType, setIsSavingMemberType] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadMembers();
    loadCustomFunctions();
  }, []);

  const loadCustomFunctions = async () => {
    const { data } = await supabase
      .from("custom_member_functions")
      .select("*")
      .order("name");
    if (data) setCustomFunctions(data);
  };

  const loadMembers = async () => {
    try {
      // Load profiles with email preferences
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*");

      if (profilesError) throw profilesError;

      // Load email preferences
      const { data: emailPrefs } = await supabase
        .from("email_preferences")
        .select("user_id, unsubscribed");

      // Load appointment counts
      const { data: appointments } = await supabase
        .from("appointments")
        .select("user_id");

      // Load admin roles
      const { data: adminRoles } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .eq("role", "admin");

      // Map admin users
      const adminUsersMap: Record<string, boolean> = {};
      adminRoles?.forEach((role) => {
        adminUsersMap[role.user_id] = true;
      });

      // Count appointments per user
      const appointmentCounts: Record<string, number> = {};
      appointments?.forEach((apt) => {
        appointmentCounts[apt.user_id] = (appointmentCounts[apt.user_id] || 0) + 1;
      });

      // Map email preferences
      const emailPrefsMap: Record<string, boolean> = {};
      emailPrefs?.forEach((pref) => {
        emailPrefsMap[pref.user_id] = pref.unsubscribed;
      });

      // Combine data
      const membersWithData = profiles?.map((profile) => ({
        ...profile,
        appointment_count: appointmentCounts[profile.id] || 0,
        unsubscribed: emailPrefsMap[profile.id] || false,
        email_confirmed: true,
        is_admin: adminUsersMap[profile.id] || false,
      })) || [];

      setMembers(membersWithData);
    } catch (error) {
      console.error("Error loading members:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar membros",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockMember = async (member: Member) => {
    try {
      const newBlockedStatus = !member.is_blocked;
      
      const { error } = await supabase
        .from("profiles")
        .update({
          is_blocked: newBlockedStatus,
          blocked_at: newBlockedStatus ? new Date().toISOString() : null,
        })
        .eq("id", member.id);

      if (error) throw error;

      setMembers(members.map((m) =>
        m.id === member.id ? { ...m, is_blocked: newBlockedStatus } : m
      ));

      toast({
        title: "Sucesso",
        description: newBlockedStatus ? "Membro bloqueado" : "Membro desbloqueado",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-member', {
        body: { memberId: memberToDelete.id }
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      // Close dialog and clear state first
      setShowDeleteDialog(false);
      setMemberToDelete(null);

      toast({
        title: "Sucesso",
        description: "Membro excluído permanentemente",
      });

      // Force reload from database to ensure UI is in sync
      await loadMembers();
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao excluir membro",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewDetails = async (member: Member) => {
    setSelectedMember(member);
    setShowDetailsDialog(true);

    const { data } = await supabase
      .from("appointments")
      .select("*")
      .eq("user_id", member.id)
      .order("appointment_date", { ascending: false });

    setMemberAppointments(data || []);
  };

  const handleUpdateMemberType = async (memberId: string, newType: 'consulente' | 'filho') => {
    setIsSavingMemberType(true);
    try {
      const updateData: any = { member_type: newType };
      
      // If changing to consulente, clear the function fields
      if (newType === 'consulente') {
        updateData.member_function = null;
        updateData.custom_function = null;
      }

      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", memberId);

      if (error) throw error;

      setMembers(members.map((m) =>
        m.id === memberId ? { ...m, ...updateData } : m
      ));

      if (selectedMember?.id === memberId) {
        setSelectedMember({ ...selectedMember, ...updateData });
      }

      toast({
        title: "Sucesso",
        description: "Tipo de membro atualizado",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingMemberType(false);
    }
  };

  const handleUpdateMemberFunction = async (memberId: string, functionValue: string) => {
    setIsSavingMemberType(true);
    try {
      // Check if it's a custom function or standard
      const isCustom = !MEMBER_FUNCTIONS.find(f => f.value === functionValue);
      
      const updateData: any = {
        member_function: isCustom ? null : functionValue,
        custom_function: isCustom ? functionValue : null,
      };

      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", memberId);

      if (error) throw error;

      setMembers(members.map((m) =>
        m.id === memberId ? { ...m, ...updateData } : m
      ));

      if (selectedMember?.id === memberId) {
        setSelectedMember({ ...selectedMember, ...updateData });
      }

      toast({
        title: "Sucesso",
        description: "Função atualizada",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingMemberType(false);
    }
  };

  const handleAddCustomFunction = async () => {
    if (!newFunctionName.trim()) return;

    try {
      const { data, error } = await supabase
        .from("custom_member_functions")
        .insert({ name: newFunctionName.trim() })
        .select()
        .single();

      if (error) throw error;

      setCustomFunctions([...customFunctions, data]);
      setNewFunctionName("");
      setShowAddFunctionDialog(false);

      toast({
        title: "Sucesso",
        description: "Nova função adicionada",
      });
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const exportToCSV = () => {
    const headers = ["Nome", "Email", "Telefone", "Data Cadastro", "Consultas", "Status", "Newsletter", "Tipo", "Função"];
    const rows = filteredMembers.map((m) => [
      m.full_name,
      m.email,
      m.phone || "",
      format(new Date(m.created_at), "dd/MM/yyyy"),
      m.appointment_count?.toString() || "0",
      m.is_blocked ? "Bloqueado" : "Ativo",
      m.unsubscribed ? "Cancelado" : "Inscrito",
      m.member_type === 'filho' ? 'Filho' : 'Consulente',
      m.custom_function || getMemberFunctionLabel(m.member_function),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `membros_tupja_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getMemberFunctionLabel = (func: string | null) => {
    if (!func) return "-";
    const found = MEMBER_FUNCTIONS.find(f => f.value === func);
    return found ? found.label : func;
  };

  // Filter and sort members
  const filteredMembers = members
    .filter((member) => {
      const matchesSearch =
        member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase());

      switch (filterType) {
        case "active":
          return matchesSearch && !member.is_blocked;
        case "blocked":
          return matchesSearch && member.is_blocked;
        case "unsubscribed":
          return matchesSearch && member.unsubscribed;
        case "consulente":
          return matchesSearch && (member.member_type === 'consulente' || !member.member_type);
        case "filho":
          return matchesSearch && member.member_type === 'filho';
        default:
          return matchesSearch;
      }
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "full_name":
          comparison = a.full_name.localeCompare(b.full_name);
          break;
        case "created_at":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case "appointment_count":
          comparison = (a.appointment_count || 0) - (b.appointment_count || 0);
          break;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{members.length}</p>
                <p className="text-sm text-muted-foreground">Total de Membros</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{members.filter((m) => (m.member_type === 'consulente' || !m.member_type) && !m.is_admin).length}</p>
                <p className="text-sm text-muted-foreground">Consulentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-full">
                <User className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{members.filter((m) => m.member_type === 'filho').length}</p>
                <p className="text-sm text-muted-foreground">Filhos da Casa</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/10 rounded-full">
                <MailX className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{members.filter((m) => m.unsubscribed).length}</p>
                <p className="text-sm text-muted-foreground">Unsubscribed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <CardTitle className="text-lg sm:text-xl">Lista de Membros</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {filteredMembers.length} membro(s)
              </CardDescription>
            </div>
            <Button onClick={exportToCSV} variant="outline" size="sm" className="w-full sm:w-auto">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={(v) => setFilterType(v as FilterType)}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="blocked">Bloqueados</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                <SelectItem value="consulente">Consulentes</SelectItem>
                <SelectItem value="filho">Filhos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="hidden sm:table-cell w-12">Avatar</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSort("full_name")}
                      className="h-auto p-0 font-medium text-xs sm:text-sm"
                    >
                      Nome
                      <ArrowUpDown className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </Button>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">Tipo</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSort("created_at")}
                      className="h-auto p-0 font-medium text-xs sm:text-sm"
                    >
                      Cadastro
                      <ArrowUpDown className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </Button>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSort("appointment_count")}
                      className="h-auto p-0 font-medium text-xs sm:text-sm"
                    >
                      Consultas
                      <ArrowUpDown className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </Button>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="text-right w-10">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="hidden sm:table-cell">
                      <Avatar className="w-8 h-8 sm:w-10 sm:h-10">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback>
                          <User className="w-4 h-4 sm:w-5 sm:h-5" />
                        </AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{member.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                        <div className="flex gap-1 mt-1 sm:hidden">
                          <Badge variant={member.is_admin ? 'destructive' : member.member_type === 'filho' ? 'default' : 'secondary'} className="text-xs">
                            {member.is_admin ? 'Admin' : member.member_type === 'filho' ? 'Filho' : 'Consulente'}
                          </Badge>
                          {member.is_blocked && (
                            <Badge variant="destructive" className="text-xs">Bloq</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant={member.is_admin ? 'destructive' : member.member_type === 'filho' ? 'default' : 'secondary'}>
                        {member.is_admin ? 'Admin' : member.member_type === 'filho' ? 'Filho' : 'Consulente'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {format(new Date(member.created_at), "dd/MM/yy", { locale: ptBR })}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{member.appointment_count || 0}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {member.is_blocked ? (
                        <Badge variant="destructive">Bloqueado</Badge>
                      ) : (
                        <Badge variant="outline">Ativo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right p-1 sm:p-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(member)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Detalhes
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => window.open(`mailto:${member.email}`, "_blank")}
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Enviar Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBlockMember(member)}>
                            <Ban className="w-4 h-4 mr-2" />
                            {member.is_blocked ? "Desbloquear" : "Bloquear"}
                          </DropdownMenuItem>
                          {!member.is_admin && (
                            <DropdownMenuItem
                              onClick={() => {
                                setMemberToDelete(member);
                                setShowDeleteDialog(true);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Member Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Membro</DialogTitle>
            <DialogDescription>
              Informações completas e configurações do membro
            </DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={selectedMember.avatar_url || undefined} />
                  <AvatarFallback>
                    <User className="w-10 h-10" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-bold">{selectedMember.full_name}</h3>
                  <p className="text-muted-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {selectedMember.email}
                  </p>
                  {selectedMember.phone && (
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {selectedMember.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Member Type and Function */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Classificação do Membro</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Conta</Label>
                      <Select
                        value={selectedMember.member_type || 'consulente'}
                        onValueChange={(v) => handleUpdateMemberType(selectedMember.id, v as 'consulente' | 'filho')}
                        disabled={isSavingMemberType}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consulente">Consulente</SelectItem>
                          <SelectItem value="filho">Filho da Casa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedMember.member_type === 'filho' && (
                      <div className="space-y-2">
                        <Label>Função</Label>
                        <Select
                          value={selectedMember.custom_function || selectedMember.member_function || ''}
                          onValueChange={(v) => handleUpdateMemberFunction(selectedMember.id, v)}
                          disabled={isSavingMemberType}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecionar função" />
                          </SelectTrigger>
                          <SelectContent>
                            {MEMBER_FUNCTIONS.map((func) => (
                              <SelectItem key={func.value} value={func.value}>
                                {func.label}
                              </SelectItem>
                            ))}
                            {customFunctions.map((func) => (
                              <SelectItem key={func.id} value={func.name}>
                                {func.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {selectedMember.member_type === 'filho' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddFunctionDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Nova Função
                    </Button>
                  )}

                  {selectedMember.member_type === 'filho' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-muted-foreground">Pai de Cabeça</p>
                        <p className="font-medium">{selectedMember.pai_de_cabeca || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Mãe de Cabeça</p>
                        <p className="font-medium">{selectedMember.mae_de_cabeca || "-"}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Data de Cadastro</p>
                  <p className="font-medium">
                    {format(new Date(selectedMember.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Total de Consultas</p>
                  <p className="font-medium">{memberAppointments.length}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Histórico de Consultas</h4>
                {memberAppointments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    Nenhuma consulta registrada
                  </p>
                ) : (
                  <div className="max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Horário</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {memberAppointments.map((apt) => (
                          <TableRow key={apt.id}>
                            <TableCell>
                              {format(new Date(apt.appointment_date), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>{apt.appointment_time.slice(0, 5)}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  apt.status === "confirmed"
                                    ? "default"
                                    : apt.status === "cancelled"
                                    ? "destructive"
                                    : "secondary"
                                }
                              >
                                {apt.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Custom Function Dialog */}
      <Dialog open={showAddFunctionDialog} onOpenChange={setShowAddFunctionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Nova Função</DialogTitle>
            <DialogDescription>
              Crie uma nova função personalizada para membros
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="function-name">Nome da Função</Label>
              <Input
                id="function-name"
                value={newFunctionName}
                onChange={(e) => setNewFunctionName(e.target.value)}
                placeholder="Ex: Ogã, Ekedi, etc."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddFunctionDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddCustomFunction} disabled={!newFunctionName.trim()}>
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o membro "{memberToDelete?.full_name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMember}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
